'use client'

import { useNamedConvexAuth } from '@brew/auth/convex-client'
import { ConvexProviderWithAuth, ConvexReactClient } from 'convex/react'
import { ConvexQueryCacheProvider } from 'convex-helpers/react/cache/provider'
import type { ReactNode } from 'react'

/**
 * Convex client for the web app.
 *
 * Wires `ConvexProviderWithAuth` to the shared named-template Clerk adapter so
 * every Convex websocket message carries the signed-in user's JWT via the
 * explicitly requested `convex` template configured in `convex/auth.config.ts`,
 * which adds the `orgId`, `orgRole`, and independent platform `role` claims.
 * Identity is read on
 * the server via `ctx.auth.getUserIdentity()` by the shared guards in
 * `convex/shared/org_auth.ts` by the canonical owner and tenant guards.
 *
 * Auth model (post-BOLA sweep): most public Convex functions now
 * HARD-REJECT anonymous and cross-tenant callers via those guards —
 * they are no longer best-effort. The JWT is attached ASYNCHRONOUSLY
 * after mount, so identity-scoped client reactive queries MUST go
 * through `hooks/use-authed-query.ts` (`useAuthedQuery` /
 * `useAuthedPaginatedQuery`), which hold the query until Convex reports
 * an authenticated identity. Firing a raw `useQuery` on a guarded
 * function during that bootstrap window throws and crashes the tree.
 * This is enforced by `tests/convex-client-auth-gating.test.ts`.
 *
 * Only intentionally-public functions (the template gallery and the
 * public unsubscribe page) tolerate anonymous reads — they degrade
 * gracefully (return `null` / empty) for a `null` identity and are
 * allowlisted in that gating test.
 */
const convex = new ConvexReactClient(
  process.env.NEXT_PUBLIC_CONVEX_URL ?? 'https://example.convex.cloud',
  {
    // Skip the eager websocket open during SSR/build; resume on
    // first client-side mount. Avoids noisy errors on build
    // pipelines that don't have a live Convex deployment in scope.
    unsavedChangesWarning: false,
  }
)

export function ConvexClientProvider({ children }: { children: ReactNode }) {
  return (
    <ConvexProviderWithAuth client={convex} useAuth={useNamedConvexAuth}>
      {/*
        Query cache: keeps Convex subscriptions warm for 5 minutes after
        their last subscriber unmounts, so tab/chat switches re-paint
        instantly from the live cache instead of cold-resubscribing
        (~0.5–1s). Consumed via the cache-aware `useQuery` inside
        `hooks/use-authed-query.ts` — components keep importing the
        authed wrappers as before.
      */}
      <ConvexQueryCacheProvider expiration={300_000}>
        {children}
      </ConvexQueryCacheProvider>
    </ConvexProviderWithAuth>
  )
}
